import {
  RiskScoreWindow,
  type GetRiskScoreFilterOptionsCommandInput,
  type ListRiskScoresCommandInput,
  type RiskScoreSortField,
  type RiskScoreSortOrder,
} from '@amzn/gbsa-helios-typescript-client';

export const RISK_SCORE_PAGE_SIZE = 600;
export const RISK_ENGINE_DEFAULT_WINDOW = RiskScoreWindow.SEVEN_DAY;

interface BuildRiskScoreRequestOptions {
  readonly businessUnitId: string | undefined;
  readonly locations?: readonly string[];
  readonly maxResults?: number;
  /** Zero-based row offset for server-side pagination. Omit for the first/only page. */
  readonly offset?: number;
  readonly timeWindow?: RiskScoreWindow | undefined;
  /** Metric-category filter (multi-select). Omitted when empty — all categories. */
  readonly categories?: readonly string[];
  /** Metric-code filter (multi-select). Omitted when empty — all metrics. */
  readonly metrics?: readonly string[];
  /** Risk-status filter (multi-select). Omitted when empty — all statuses. */
  readonly statuses?: readonly string[];
  /** Sort key. Omitted to keep the default riskiest-first order. */
  readonly sortBy?: RiskScoreSortField;
  /** Sort direction. Only emitted alongside `sortBy`. */
  readonly sortOrder?: RiskScoreSortOrder;
}

/** Builds the SDK request without explicit undefined optional properties. */
export function buildRiskScoreRequest({
  businessUnitId,
  locations,
  maxResults = RISK_SCORE_PAGE_SIZE,
  offset,
  timeWindow = RISK_ENGINE_DEFAULT_WINDOW,
  categories,
  metrics,
  statuses,
  sortBy,
  sortOrder,
}: BuildRiskScoreRequestOptions): ListRiskScoresCommandInput {
  return {
    businessUnitId: businessUnitId ?? '',
    timeWindow,
    maxResults,
    ...(offset !== undefined ? { offset } : {}),
    ...(locations && locations.length > 0 ? { locations: [...locations] } : {}),
    ...(categories && categories.length > 0 ? { categories: [...categories] } : {}),
    ...(metrics && metrics.length > 0 ? { metrics: [...metrics] } : {}),
    ...(statuses && statuses.length > 0 ? { statuses: [...statuses] } : {}),
    ...(sortBy ? { sortBy, ...(sortOrder ? { sortOrder } : {}) } : {}),
  };
}

interface BuildFilterOptionsRequestOptions {
  readonly businessUnitId: string | undefined;
  readonly locations?: readonly string[];
  readonly timeWindow?: RiskScoreWindow | undefined;
  readonly categories?: readonly string[];
  readonly metrics?: readonly string[];
  readonly statuses?: readonly string[];
}

/**
 * Builds the faceted filter-options request. Mirrors {@link buildRiskScoreRequest}'s location and
 * selection handling so the option counts are scoped to the same window and stations as the list,
 * but carries no paging or sort — it only describes the current cross-field selection.
 */
export function buildRiskScoreFilterOptionsRequest({
  businessUnitId,
  locations,
  timeWindow = RISK_ENGINE_DEFAULT_WINDOW,
  categories,
  metrics,
  statuses,
}: BuildFilterOptionsRequestOptions): GetRiskScoreFilterOptionsCommandInput {
  return {
    businessUnitId: businessUnitId ?? '',
    timeWindow,
    ...(locations && locations.length > 0 ? { locations: [...locations] } : {}),
    ...(categories && categories.length > 0 ? { categories: [...categories] } : {}),
    ...(metrics && metrics.length > 0 ? { metrics: [...metrics] } : {}),
    ...(statuses && statuses.length > 0 ? { statuses: [...statuses] } : {}),
  };
}
